import { Button, Callout, Flex, Link, Text, TextField } from "@radix-ui/themes";
import { useMutation } from "@tanstack/react-query";
import { Formik } from "formik";
import { useMemo } from "react";
import { Navigate, NavLink, useNavigate } from "react-router-dom";
import { RegisterRequestDto } from "use-smart-locks-shared";
import * as Yup from "yup";
import { routes } from "../../../routes";
import {
  getApiUserErrorMessage,
  registerAccount,
} from "../../../shared/api/nest-api";
import { FullSpinner } from "../../../shared/components/full-spinner";
import { AuthRoot } from "../auth-root.component";
import { buildLoginRedirectPath } from "../client/login-redirect";
import { AuthErrorMessage } from "../components/auth-error-message.component";
import {
  createEmailValidation,
  germanPostalCodeValidation,
  internationalPhoneNumberValidation,
  strongPasswordValidation,
} from "../validation/auth-validation";
import {
  getAuthStationRestrictionMessage,
  useAuthPageContext,
} from "./use-auth-page-context";
import { useIsLoggedInWithStoredToken } from "./use-is-logged-in-with-stored-token";

type RegisterInput = RegisterRequestDto & {
  stationSlug?: string;
};

export function RegisterPage() {
  const navigate = useNavigate();
  const shouldRedirectAuthenticatedUser = useIsLoggedInWithStoredToken();
  const {
    nextPath,
    stationSlug,
    allowedEmailDomains,
    isLoadingStationContext,
    stationContextErrorMessage,
  } = useAuthPageContext();
  const registerValidationSchema = useMemo(
    () =>
      Yup.object({
        name: Yup.string().trim().required("Name ist erforderlich"),
        email: createEmailValidation(allowedEmailDomains),
        password: strongPasswordValidation,
        phoneNumber: internationalPhoneNumberValidation,
        street: Yup.string().trim().required("Straße ist erforderlich"),
        houseNumber: Yup.string()
          .trim()
          .required("Hausnummer ist erforderlich"),
        postalCode: germanPostalCodeValidation,
        city: Yup.string().trim().required("Ort ist erforderlich"),
      }),
    [allowedEmailDomains],
  );
  const registerMutation = useMutation({
    mutationFn: async (input: RegisterInput) => {
      await registerAccount(input);
    },
    onSuccess: (_data, variables) => {
      void navigate(
        `${routes.auth.verificationSent}?email=${encodeURIComponent(variables.email)}`,
      );
    },
  });

  if (shouldRedirectAuthenticatedUser) {
    return <Navigate to={nextPath} replace />;
  }

  if (isLoadingStationContext) {
    return (
      <AuthRoot title="Konto erstellen">
        <FullSpinner />
      </AuthRoot>
    );
  }

  return (
    <AuthRoot title="Konto erstellen">
      {allowedEmailDomains.length > 0 && (
        <Callout.Root color="blue">
          <Callout.Text>
            {getAuthStationRestrictionMessage(allowedEmailDomains)}
          </Callout.Text>
        </Callout.Root>
      )}
      {stationContextErrorMessage && (
        <AuthErrorMessage message={stationContextErrorMessage} />
      )}
      <Formik
        initialValues={{
          name: "",
          email: "",
          password: "",
          phoneNumber: "",
          street: "",
          houseNumber: "",
          postalCode: "",
          city: "",
        }}
        validationSchema={registerValidationSchema}
        validateOnMount={false}
        onSubmit={(values) => {
          registerMutation.mutate({
            name: values.name,
            email: values.email,
            password: values.password,
            phoneNumber: values.phoneNumber,
            street: values.street,
            houseNumber: values.houseNumber,
            postalCode: values.postalCode,
            city: values.city,
            stationSlug,
          });
        }}
      >
        {({
          values,
          errors,
          touched,
          dirty,
          handleBlur,
          handleChange,
          handleSubmit,
        }) => (
          <form onSubmit={handleSubmit}>
            <Flex direction="column" gap="3">
              <Flex direction="column" gap="1">
                <Text
                  as="label"
                  size="2"
                  weight="medium"
                  htmlFor="register-name"
                >
                  Name
                </Text>
                <TextField.Root
                  id="register-name"
                  required
                  size="3"
                  placeholder="Name"
                  name="name"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {touched.name && errors.name && (
                  <Text size="1" color="red">
                    {errors.name}
                  </Text>
                )}
              </Flex>
              <Flex direction="column" gap="1">
                <Text
                  as="label"
                  size="2"
                  weight="medium"
                  htmlFor="register-email"
                >
                  E-Mail
                </Text>
                <TextField.Root
                  id="register-email"
                  required
                  type="email"
                  size="3"
                  placeholder="E-Mail"
                  name="email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {touched.email && errors.email && (
                  <Text size="1" color="red">
                    {errors.email}
                  </Text>
                )}
              </Flex>
              <Flex direction="column" gap="1">
                <Text
                  as="label"
                  size="2"
                  weight="medium"
                  htmlFor="register-password"
                >
                  Passwort
                </Text>
                <TextField.Root
                  id="register-password"
                  required
                  type="password"
                  size="3"
                  placeholder="Passwort"
                  name="password"
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {touched.password && errors.password && (
                  <Text size="1" color="red">
                    {errors.password}
                  </Text>
                )}
              </Flex>
              <Flex direction="column" gap="1">
                <Text
                  as="label"
                  size="2"
                  weight="medium"
                  htmlFor="register-phone-number"
                >
                  Telefonnummer
                </Text>
                <TextField.Root
                  id="register-phone-number"
                  required
                  size="3"
                  placeholder="+49 151 12345678"
                  name="phoneNumber"
                  value={values.phoneNumber}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {touched.phoneNumber && errors.phoneNumber && (
                  <Text size="1" color="red">
                    {errors.phoneNumber}
                  </Text>
                )}
              </Flex>
              <Flex direction="column" gap="1">
                <Text
                  as="label"
                  size="2"
                  weight="medium"
                  htmlFor="register-street"
                >
                  Straße
                </Text>
                <TextField.Root
                  id="register-street"
                  required
                  size="3"
                  placeholder="Straße"
                  name="street"
                  value={values.street}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {touched.street && errors.street && (
                  <Text size="1" color="red">
                    {errors.street}
                  </Text>
                )}
              </Flex>
              <Flex direction="column" gap="1">
                <Text
                  as="label"
                  size="2"
                  weight="medium"
                  htmlFor="register-house-number"
                >
                  Hausnummer
                </Text>
                <TextField.Root
                  id="register-house-number"
                  required
                  size="3"
                  placeholder="Hausnummer"
                  name="houseNumber"
                  value={values.houseNumber}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {touched.houseNumber && errors.houseNumber && (
                  <Text size="1" color="red">
                    {errors.houseNumber}
                  </Text>
                )}
              </Flex>
              <Flex direction="column" gap="1">
                <Text
                  as="label"
                  size="2"
                  weight="medium"
                  htmlFor="register-postal-code"
                >
                  PLZ
                </Text>
                <TextField.Root
                  id="register-postal-code"
                  required
                  size="3"
                  placeholder="PLZ"
                  name="postalCode"
                  value={values.postalCode}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {touched.postalCode && errors.postalCode && (
                  <Text size="1" color="red">
                    {errors.postalCode}
                  </Text>
                )}
              </Flex>
              <Flex direction="column" gap="1">
                <Text
                  as="label"
                  size="2"
                  weight="medium"
                  htmlFor="register-city"
                >
                  Ort
                </Text>
                <TextField.Root
                  id="register-city"
                  required
                  size="3"
                  placeholder="Ort"
                  name="city"
                  value={values.city}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {touched.city && errors.city && (
                  <Text size="1" color="red">
                    {errors.city}
                  </Text>
                )}
              </Flex>
              <Flex direction="column" gap="1">
                <Text
                  as="label"
                  size="2"
                  weight="medium"
                  htmlFor="register-country"
                >
                  Land
                </Text>
                <TextField.Root
                  id="register-country"
                  size="3"
                  value="Deutschland"
                  disabled
                />
              </Flex>
              <Button
                type="submit"
                size="3"
                variant="soft"
                loading={registerMutation.isPending}
                disabled={
                  !dirty ||
                  registerMutation.isPending ||
                  !!stationContextErrorMessage
                }
              >
                Registrieren
              </Button>
            </Flex>
          </form>
        )}
      </Formik>
      <Flex justify="between" align="center">
        <Link asChild>
          <NavLink to={buildLoginRedirectPath(nextPath, stationSlug)}>
            Zum Login
          </NavLink>
        </Link>
      </Flex>
      {registerMutation.isError && (
        <AuthErrorMessage
          error={registerMutation.error}
          message={getApiUserErrorMessage(registerMutation.error)}
        />
      )}
    </AuthRoot>
  );
}
